import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSearch, FiFileText, FiExternalLink } from "react-icons/fi";
import scholarshipService from "../../services/scholarshipService";
import studentService from "../../services/studentService";
import { academicYearService } from "../../services/yearService";
import { useApp } from "../../context/AppContext";

const MAX_FILE_SIZE = 3 * 1024 * 1024;
const STATUS_OPTIONS = ["Applied", "Approved", "Disbursed", "Rejected"];

function StudentPicker({ selected, onSelect, disabled }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await studentService.getAll({ search: query, limit: 8 });
                setResults(res.data.students || res.data.data || res.data || []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (disabled) {
        return (
            <div>
                <label className="form-label">Student</label>
                <div className="student-select-box" style={{ padding: "0.6rem 0.75rem", fontSize: 13.5 }}>
                    <strong>{selected?.fullName || "—"}</strong>
                    {selected?.enrollmentNo ? ` · ${selected.enrollmentNo}` : ""}
                </div>
            </div>
        );
    }

    return (
        <div ref={boxRef} style={{ position: "relative" }}>
            <label className="form-label">
                Student<span style={{ color: "var(--danger)" }}> *</span>
            </label>
            {selected ? (
                <div
                    className="student-select-box"
                    style={{ padding: "0.6rem 0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <span style={{ fontSize: 13.5 }}>
                        <strong>{selected.fullName}</strong>
                        {selected.enrollmentNo ? ` · ${selected.enrollmentNo}` : ""}
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => onSelect(null)}>
                        Change
                    </button>
                </div>
            ) : (
                <div className="student-select-box">
                    <div className="student-select-search">
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input
                            placeholder="Search student by name, enrollment no, or roll no..."
                            value={query}
                            onFocus={() => setOpen(true)}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setOpen(true);
                            }}
                        />
                    </div>
                    {open && (
                        <div className="student-select-list">
                            {loading && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>Searching...</div>
                            )}
                            {!loading && query.trim() && results.length === 0 && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>No students found.</div>
                            )}
                            {results.map((student) => (
                                <div
                                    key={student._id}
                                    className="student-select-item"
                                    onClick={() => {
                                        onSelect(student);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                >
                                    {student.fullName}
                                    {student.enrollmentNo ? ` · ${student.enrollmentNo}` : ""}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ScholarshipForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const preselectedStudentId = searchParams.get("studentId");
    const isEdit = Boolean(id);
    const { academicYear: currentAcademicYear } = useApp();

    const [student, setStudent] = useState(null);
    const [academicYears, setAcademicYears] = useState([]);
    const [form, setForm] = useState({
        academicYear: currentAcademicYear?._id || "",
        schemeType: "",
        sanctioningAuthority: "",
        sanctionedAmount: "",
        disbursedAmount: "0",
        disbursedDate: "",
        status: "Applied",
        remarks: "",
        sanctionLetterUrl: "",
        sanctionLetterDataUrl: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    useEffect(() => {
        (async () => {
            try {
                const res = await academicYearService.getAll();
                setAcademicYears(res.data || []);
            } catch (err) {
                toast.error("Couldn't load academic years: " + err.message);
            }
        })();
    }, []);

    useEffect(() => {
        if (isEdit || !preselectedStudentId) return;
        (async () => {
            try {
                const res = await studentService.getById(preselectedStudentId);
                setStudent(res.data);
            } catch (err) {
                toast.error("Couldn't load the linked student: " + err.message);
            }
        })();
    }, [isEdit, preselectedStudentId]);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await scholarshipService.getById(id);
                const s = res.data;
                setStudent(s.student || null);
                setForm({
                    academicYear: s.academicYear?._id || s.academicYear || "",
                    schemeType: s.schemeType || "",
                    sanctioningAuthority: s.sanctioningAuthority || "",
                    sanctionedAmount: s.sanctionedAmount ?? "",
                    disbursedAmount: s.disbursedAmount ?? 0,
                    disbursedDate: s.disbursedDate || "",
                    status: s.status || "Applied",
                    remarks: s.remarks || "",
                    sanctionLetterUrl: s.sanctionLetterUrl || "",
                    sanctionLetterDataUrl: "",
                });
            } catch (err) {
                toast.error(err.message);
                navigate("/scholarships");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const readFileAsDataUrl = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error("Failed to read file."));
            reader.readAsDataURL(file);
        });

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE) {
            toast.error("File must be 3 MB or smaller.");
            e.target.value = "";
            return;
        }
        try {
            const dataUrl = await readFileAsDataUrl(file);
            setForm((prev) => ({ ...prev, sanctionLetterDataUrl: dataUrl, sanctionLetterUrl: "" }));
        } catch (err) {
            toast.error(err.message);
        } finally {
            e.target.value = "";
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!student) nextErrors.student = "Select a student";
        if (!form.academicYear) nextErrors.academicYear = "Academic year is required";
        if (!form.schemeType.trim()) nextErrors.schemeType = "Scheme name is required";
        if (form.sanctionedAmount === "" || Number(form.sanctionedAmount) <= 0) {
            nextErrors.sanctionedAmount = "Enter a valid sanctioned amount";
        }
        if (Number(form.disbursedAmount) > Number(form.sanctionedAmount || 0)) {
            nextErrors.disbursedAmount = "Disbursed amount can't exceed sanctioned amount";
        }
        if (form.disbursedDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(form.disbursedDate)) {
            nextErrors.disbursedDate = "Use DD/MM/YYYY format";
        }
        return nextErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = validate();
        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error(Object.values(nextErrors)[0]);
            return;
        }

        const payload = {
            student: student._id,
            academicYear: form.academicYear,
            schemeType: form.schemeType.trim(),
            sanctioningAuthority: form.sanctioningAuthority.trim(),
            sanctionedAmount: Number(form.sanctionedAmount),
            disbursedAmount: Number(form.disbursedAmount || 0),
            disbursedDate: form.disbursedDate,
            status: form.status,
            remarks: form.remarks,
            sanctionLetterDataUrl: form.sanctionLetterDataUrl || undefined,
        };

        setLoading(true);
        try {
            if (isEdit) {
                await scholarshipService.update(id, payload);
                toast.success("Scholarship updated.");
            } else {
                await scholarshipService.create(payload);
                toast.success("Scholarship added.");
            }
            navigate("/scholarships");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="card">
                <div className="loading-center">
                    <div className="spinner" />
                    Loading scholarship...
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Scholarship" : "Add Scholarship"}</h1>
                    <p className="page-subtitle">Track sanction and disbursement details for a student's scholarship.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="card">
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <StudentPicker selected={student} onSelect={setStudent} disabled={isEdit} />
                                {errors.student && <span className="form-error">{errors.student}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Academic Year<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <select
                                    className={`form-control ${errors.academicYear ? "error" : ""}`}
                                    name="academicYear"
                                    value={form.academicYear}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Academic Year</option>
                                    {academicYears.map((y) => (
                                        <option key={y._id} value={y._id}>
                                            {y.label}
                                        </option>
                                    ))}
                                </select>
                                {errors.academicYear && <span className="form-error">{errors.academicYear}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Scheme Name<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    className={`form-control ${errors.schemeType ? "error" : ""}`}
                                    name="schemeType"
                                    placeholder="e.g. EBC Scholarship"
                                    value={form.schemeType}
                                    onChange={handleChange}
                                />
                                {errors.schemeType && <span className="form-error">{errors.schemeType}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Sanctioning Authority</label>
                                <input
                                    className="form-control"
                                    name="sanctioningAuthority"
                                    placeholder="e.g. Social Welfare Department"
                                    value={form.sanctioningAuthority}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Status</label>
                                <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Sanctioned Amount (₹)<span style={{ color: "var(--danger)" }}> *</span>
                                </label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.sanctionedAmount ? "error" : ""}`}
                                    name="sanctionedAmount"
                                    value={form.sanctionedAmount}
                                    onChange={handleChange}
                                />
                                {errors.sanctionedAmount && <span className="form-error">{errors.sanctionedAmount}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Disbursed Amount (₹)</label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.disbursedAmount ? "error" : ""}`}
                                    name="disbursedAmount"
                                    value={form.disbursedAmount}
                                    onChange={handleChange}
                                />
                                {errors.disbursedAmount && <span className="form-error">{errors.disbursedAmount}</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Disbursed Date</label>
                                <input
                                    className={`form-control ${errors.disbursedDate ? "error" : ""}`}
                                    name="disbursedDate"
                                    placeholder="DD/MM/YYYY"
                                    value={form.disbursedDate}
                                    onChange={handleChange}
                                />
                                {errors.disbursedDate && <span className="form-error">{errors.disbursedDate}</span>}
                            </div>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Sanction Letter / Proof (optional)</label>
                                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                                    <input type="file" accept="image/*,.pdf" className="form-control" onChange={handleFileChange} style={{ maxWidth: 320 }} />
                                    {form.sanctionLetterUrl && (
                                        <a href={form.sanctionLetterUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">
                                            <FiExternalLink size={12} style={{ marginRight: 4 }} />
                                            View current file
                                        </a>
                                    )}
                                    {form.sanctionLetterDataUrl && (
                                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                            <FiFileText size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
                                            New file selected — will upload on save
                                        </span>
                                    )}
                                </div>
                                <span className="form-hint">Image or PDF, up to 3 MB.</span>
                            </div>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Remarks</label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    name="remarks"
                                    value={form.remarks}
                                    onChange={handleChange}
                                    style={{ resize: "vertical" }}
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "1.25rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate("/scholarships")}>
                                Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                {loading ? "Saving..." : isEdit ? "Update Scholarship" : "Add Scholarship"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}